import { useUpdateTasks } from 'features/tasks';
import React, { useMemo } from 'react';
import { BellNotifications16 } from 'shared/assets/icons';
import { PopupMenu } from 'shared/components/ui/popup-menu';
import { TimePickerMenu } from 'shared/components/ui/time-picker-menu';
import { reminderTimes } from 'shared/constants';
import { useUser } from 'shared/hooks/use-user';
import { Task } from 'shared/types/task';
import { getTimeFormat } from 'shared/utils/get-time-format';

export type ReminderTimeSubMenuProps = Omit<
  React.ComponentProps<typeof PopupMenu>,
  'children' | 'onOutside'
> & {
  tasks: Task[];
  onClose: () => void;
};

export const ReminderTimeSubMenu: React.FC<ReminderTimeSubMenuProps> = ({
  tasks,
  onClose,
  ...rest
}) => {
  const { submit: updateTasks } = useUpdateTasks();
  const user = useUser();
  const timeFormat = getTimeFormat(user);

  // Determine initial value: show value only if all tasks share the same reminderTime
  const value = useMemo<number | undefined>(() => {
    const firstTask = tasks[0];
    const firstValue = firstTask?.reminderTime;

    if (!firstValue) {
      return undefined;
    }

    // Check if all tasks have the same value
    const allSame = tasks.every(
      (task) => !task.reminderTime || task.reminderTime === firstValue,
    );

    return allSame ? firstValue : undefined;
  }, [tasks]);

  const onChange = (time?: number) => {
    updateTasks(
      tasks.map((task) => ({
        id: task.id,
        reminderTime: time ?? null,
      })),
    );
    onClose();
  };

  return (
    <TimePickerMenu
      {...rest}
      timeFormat={timeFormat}
      value={value}
      onChange={onChange}
      onCancel={onClose}
      options={reminderTimes}
      optionIcon={BellNotifications16}
      hasCustomInput
      canClear
    />
  );
};
